import { DOCUMENT, Injectable, inject, signal } from '@angular/core';
import { LOCAL_STORAGE } from '@ng-web-apis/common';

/** The colour scheme the user chose; `system` follows the operating system. */
export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_PREFERENCES: readonly ThemePreference[] = [
  'light',
  'dark',
  'system',
];

const STORAGE_KEY = 'hunter.theme';

/** The value of `color-scheme` on the root element for each choice. */
const COLOR_SCHEME: Record<ThemePreference, string> = {
  light: 'light',
  dark: 'dark',
  system: 'light dark',
};

/**
 * The colour scheme of the app, chosen in the user menu and kept per browser.
 * It sets `color-scheme` on the root element, which the colour properties
 * (`light-dark()`) and the Material theme follow, and `data-theme` for the few
 * rules that need a selector (styles/utilities/_mixins.scss, `dark-scheme`).
 * A storage that cannot be read or written only loses the choice.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private storage = inject<Storage>(LOCAL_STORAGE);
  private document = inject(DOCUMENT);
  private current = signal<ThemePreference>(this.read());

  public readonly preference = this.current.asReadonly();

  constructor() {
    this.apply(this.current());
  }

  public set(preference: ThemePreference) {
    this.current.set(preference);
    this.apply(preference);
    try {
      if (preference === 'system') {
        this.storage.removeItem(STORAGE_KEY);
      } else {
        this.storage.setItem(STORAGE_KEY, preference);
      }
    } catch {
      // The choice holds until the page is reloaded.
    }
  }

  private apply(preference: ThemePreference) {
    const root = this.document.documentElement;
    root.style.colorScheme = COLOR_SCHEME[preference];
    if (preference === 'system') {
      delete root.dataset['theme'];
    } else {
      root.dataset['theme'] = preference;
    }
  }

  private read(): ThemePreference {
    try {
      const value = this.storage.getItem(STORAGE_KEY);
      return value === 'light' || value === 'dark' ? value : 'system';
    } catch {
      return 'system';
    }
  }
}
